import { motion } from "framer-motion";

export const AboutSection = () => {
  return (
    <section
      id="about"
      className="section-dark py-24 md:py-32"
      data-testid="about-section"
    >
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-gold/80 font-manrope text-sm tracking-[0.3em] uppercase mb-4">
            The Divine Art
          </p>
          <h2 className="font-cinzel text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6">
            About <span className="text-gold">Bharatanatyam</span>
          </h2>
          <div className="section-divider mx-auto" />
        </motion.div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Column - Main Text */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="gold-border-left pl-6">
              <p className="font-manrope text-white/80 text-lg leading-relaxed">
                Bharatanatyam, one of the oldest classical dance forms of India, 
                is a divine expression that originated in the temples of Tamil Nadu 
                over 2000 years ago. This sacred art form is a harmonious blend of 
                <span className="text-gold"> Bhava </span>(expression), 
                <span className="text-gold"> Raga </span>(melody), 
                <span className="text-gold"> Tala </span>(rhythm), and 
                <span className="text-gold"> Natya </span>(drama).
              </p>
            </div>

            <p className="font-manrope text-white/60 text-base leading-relaxed">
              More than just a dance, Bharatanatyam is a spiritual discipline that 
              connects the dancer to the divine. Every movement, every gesture, 
              every expression is a prayer offered to the cosmos. The art form 
              preserves ancient stories from Hindu mythology and philosophy, 
              keeping alive a rich cultural heritage that spans millennia.
            </p>

            <p className="font-manrope text-white/60 text-base leading-relaxed">
              At Shri Kala Vrinda, we honor this sacred tradition while making it 
              accessible to learners of all ages. Our approach combines rigorous 
              classical training with an understanding of the spiritual essence 
              that makes Bharatanatyam truly divine.
            </p>
          </motion.div>

          {/* Right Column - Significance Cards */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Card 1 */}
            <div className="card-hover bg-[#121212] border border-white/5 p-6 hover:border-gold/30">
              <h3 className="font-cinzel text-xl text-gold mb-3">Nritta</h3>
              <p className="font-manrope text-white/60 text-sm leading-relaxed">
                Pure dance - the rhythmic movement of the body in space. 
                Nritta showcases technical mastery through intricate footwork 
                and geometric patterns.
              </p>
            </div>

            {/* Card 2 */}
            <div className="card-hover bg-[#121212] border border-white/5 p-6 hover:border-gold/30">
              <h3 className="font-cinzel text-xl text-gold mb-3">Nritya</h3>
              <p className="font-manrope text-white/60 text-sm leading-relaxed">
                Expressive dance - where rhythm meets emotion. Through hand 
                gestures (mudras) and facial expressions, stories come alive.
              </p>
            </div>

            {/* Card 3 */}
            <div className="card-hover bg-[#121212] border border-white/5 p-6 hover:border-gold/30">
              <h3 className="font-cinzel text-xl text-gold mb-3">Natya</h3>
              <p className="font-manrope text-white/60 text-sm leading-relaxed">
                Dramatic dance - the complete theatrical experience combining 
                dance, music, and storytelling to portray mythology and philosophy.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Gratitude Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <div className="max-w-3xl mx-auto">
            <p className="font-playfair text-xl md:text-2xl text-white/70 italic leading-relaxed">
              "We bow in gratitude to Lord Nataraja, the cosmic dancer, and to all 
              the Gurus who have preserved and passed down this sacred art through 
              generations. Through Bharatanatyam, we seek not just artistic excellence, 
              but a path to spiritual awakening."
            </p>
            <div className="section-divider mx-auto mt-8" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
